import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, TextField, MenuItem, Alert, Checkbox, FormControlLabel, Typography } from '@mui/material';
import {
  FormShell,
  FormActions,
  SectionLabel,
  EmptyOption,
  useLocationPicker,
  inputSx,
} from '../FormUI/RoadAccidentFormUI';
import useMunicipalityCascade from '../FormUI/useMunicipalityCascade';
import MunicipalityFields from '../FormUI/MunicipalityFields';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Matches the facility types already used in the health_facilities reference
// dataset (see Hospitals.js colorMap) so new submissions stay consistent.
const facilityTypeOptions = [
  { value: 'Health Post', code: 'HP' },
  { value: 'Community Health Center', code: 'CHC' },
  { value: 'Community Health Center (Internment)', code: 'CHCI' },
  { value: 'Clinic', code: 'CL' },
  { value: 'Clinic and Pharmacy', code: 'CL & PHA' },
  { value: 'Pharmacy', code: 'PHA' },
  { value: 'Referral Hospital', code: 'RH' },
  { value: 'Regional Hospital', code: 'RGH' },
  { value: 'National Hospital', code: 'NH' },
  { value: 'Disability Rehabilitation Center', code: 'DRHC' },
  { value: 'Multi Drug Rehabilitation Center', code: 'MDRHC' },
  { value: 'Mental Rehabilitation Center', code: 'MRHC' },
];

const propertyTypeOptions = ['Private', 'Government'];

const initialFormData = {
  name: '',
  facility_type: '',
  code: '',
  location: '',
  latitude: '',
  longitude: '',
  elevation: '',
  property_type: '',
  services: '',
  operating_days: '',
  operating_hours: '',
  total_beds: '',
  maternity_beds: '',
  has_ambulance: false,
  has_emergency: false,
};

function AddrecordHospital() {
  const navigate = useNavigate();
  const cascade = useMunicipalityCascade();
  const [formData, setFormData] = useState(initialFormData);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFacilityTypeChange = (e) => {
    const value = e.target.value;
    const matched = facilityTypeOptions.find((option) => option.value === value);
    setFormData((prev) => ({ ...prev, facility_type: value, code: matched ? matched.code : '' }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleLocationChange = (latitude, longitude) => {
    setFormData((prev) => ({ ...prev, latitude, longitude }));
  };

  const handleLocationResolved = (adm) => {
    if (adm) cascade.applyResolvedAdm(adm.adm1_code, adm.adm2_code, adm.adm3_code);
  };

  const handleLandmark = (landmark) => {
    setFormData((prev) => ({ ...prev, location: landmark || '' }));
  };

  const { latLngFields, mapElement, locationError, landmarkNotFound } = useLocationPicker({
    latitude: formData.latitude,
    longitude: formData.longitude,
    onChange: handleLocationChange,
    onLocationResolved: handleLocationResolved,
    onLandmark: handleLandmark,
    infoText: 'Click on the map to set the facility location.',
  });

  const handleReset = () => {
    setFormData(initialFormData);
    cascade.reset();
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleBackClick = () => {
    navigate('/Roadaccident/Reports?tab=hospital');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    const dataToSend = new FormData();
    dataToSend.append('name', formData.name);
    dataToSend.append('facility_type', formData.facility_type);
    dataToSend.append('code', formData.code);
    dataToSend.append('municipality', cascade.selectedMunicipality);
    dataToSend.append('submunicipality', cascade.selectedSubMunicipality);
    dataToSend.append('suco', cascade.selectedSuco);
    dataToSend.append('location', formData.location);
    dataToSend.append('latitude', formData.latitude);
    dataToSend.append('longitude', formData.longitude);
    dataToSend.append('elevation', formData.elevation);
    dataToSend.append('property_type', formData.property_type);
    dataToSend.append('services', formData.services);
    dataToSend.append('operating_days', formData.operating_days);
    dataToSend.append('operating_hours', formData.operating_hours);
    dataToSend.append('total_beds', formData.total_beds);
    dataToSend.append('maternity_beds', formData.maternity_beds);
    dataToSend.append('has_ambulance', formData.has_ambulance);
    dataToSend.append('has_emergency', formData.has_emergency);

    try {
      const response = await axios.post(
        `${backendUrl}roadaccident/submit_hospital_record/`,
        dataToSend,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit. Please try again.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || JSON.stringify(error.response?.data) || error.message;
      setSubmitError(`An error occurred while submitting: ${errorMessage}`);
    }
  };

  return (
    <FormShell title="Add Hospital Record" subtitle="Register a hospital / health facility" onBack={handleBackClick} onSubmit={handleSubmit}>
      {submitSuccess && <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>Hospital Record added successfully.</Alert>}
      {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

      <Box>
        <SectionLabel text="Facility Details" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
          <TextField
            label="Facility Name"
            size="small"
            fullWidth
            sx={inputSx}
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter facility name"
          />
          <TextField
            label="Facility Type"
            select
            size="small"
            fullWidth
            sx={inputSx}
            name="facility_type"
            value={formData.facility_type}
            onChange={handleFacilityTypeChange}
          >
            <EmptyOption text="Select a facility type" />
            {facilityTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.value}</MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
          <TextField
            label="Facility Code"
            size="small"
            fullWidth
            sx={inputSx}
            name="code"
            value={formData.code}
            InputProps={{ readOnly: true }}
            helperText="Set automatically from the facility type"
          />
          <TextField
            label="Property Type"
            select
            size="small"
            fullWidth
            sx={inputSx}
            name="property_type"
            value={formData.property_type}
            onChange={handleChange}
          >
            <EmptyOption text="Select a property type" />
            {propertyTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
        {/* LEFT: Location Information */}
        <Box>
          <SectionLabel text="Location Information" />

          {locationError && !(cascade.selectedMunicipality && cascade.selectedSubMunicipality && cascade.selectedSuco) && (
            <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>
          )}

          <MunicipalityFields cascade={cascade} />
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              sx={inputSx}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location details"
            />
            {landmarkNotFound && !formData.location && (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {latLngFields}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Elevation (m)"
              type="number"
              size="small"
              fullWidth
              sx={inputSx}
              name="elevation"
              value={formData.elevation}
              onChange={handleChange}
            />
          </Box>
        </Box>

        {/* RIGHT: Map */}
        <Box>{mapElement}</Box>
      </Box>

      <Box>
        <SectionLabel text="Operating Details" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
          <TextField
            label="Operating Days"
            size="small"
            fullWidth
            sx={inputSx}
            name="operating_days"
            value={formData.operating_days}
            onChange={handleChange}
            placeholder="e.g. Monday - Friday"
          />
          <TextField
            label="Operating Hours"
            size="small"
            fullWidth
            sx={inputSx}
            name="operating_hours"
            value={formData.operating_hours}
            onChange={handleChange}
            placeholder="e.g. 08:00 - 17:00"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
          <TextField
            label="Total Beds"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="total_beds"
            value={formData.total_beds}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Maternity Beds"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="maternity_beds"
            value={formData.maternity_beds}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={formData.has_ambulance} onChange={handleCheckboxChange} name="has_ambulance" />}
            label="Has Ambulance"
          />
          <FormControlLabel
            control={<Checkbox checked={formData.has_emergency} onChange={handleCheckboxChange} name="has_emergency" />}
            label="Has Emergency"
          />
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <TextField
            label="Services"
            multiline
            rows={3}
            fullWidth
            sx={inputSx}
            name="services"
            value={formData.services}
            onChange={handleChange}
            placeholder="Describe the services offered at this facility"
          />
        </Box>
      </Box>

      <FormActions onReset={handleReset} submitLabel="Submit" />
    </FormShell>
  );
}

export default AddrecordHospital;
